import { ALLOWED_ATTACHMENT_TYPES } from "./constants";
import {
  AttachmentPreviewItem,
  CallMessage,
  EmailThread,
  FaqSource,
  FeedbackMessage,
  LeadDetails,
  MentionableUser,
  Message,
  Lead as ReduxLead,
  ResetModalityAfterMentionParams,
  V2UserListEntry,
} from "./types";

export const isHTML = (
  text: string,
  isLatestMessage: boolean = false
): boolean => {
  if (isLatestMessage) {
    return /<[a-z][\s\S]*>/i.test(text);
  }
  // return /<(?!br\s*\/?)[a-z][\s\S]*>/i.test(text);
  return /<\s*(\/?\s*(html|head|body|div|p|span|a|img|table|ul|ol|li|br|strong|em|h[1-6]))[\s>]/i.test(
    text
  );
};

// Strip common markdown formatting markers so previews show clean plain text.
const stripMarkdown = (text: string): string =>
  text
    ?.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1") // images: ![alt](url) -> alt
    ?.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links: [text](url) -> text
    ?.replace(/\*\*([^*]+)\*\*/g, "$1") // bold: **text** -> text
    ?.replace(/__([^_]+)__/g, "$1") // bold: __text__ -> text
    ?.replace(/\*([^*]+)\*/g, "$1") // italic: *text* -> text
    ?.replace(/~~([^~]+)~~/g, "$1") // strikethrough: ~~text~~ -> text
    ?.replace(/`([^`]+)`/g, "$1"); // inline code: `text` -> text

export const extractPlainTextFromHtml = (
  content: string,
  isLatestMessage: boolean = false
): string => {
  if (!content) return "";
  if (!isHTML(content, isLatestMessage)) {
    return stripMarkdown(content);
  }

  const bodyMatch = content?.match(/<body[^>]*>([\s\S]*?)<\/body>/i); // only consider the contents within body tag
  if (bodyMatch) {
    content = bodyMatch[1];
  }
  let text = content?.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
  text = text?.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");

  text = text?.replace(/<br\s*\/?>/gi, " "); // Replace <br> tags with spaces
  text = text?.replace(/<[^>]*>/g, "");
  text = text?.replace(/&[^;]+;/g, "");
  return stripMarkdown(text?.replace(/\s+/g, " ").trim());
};

export const isImage = (item: AttachmentPreviewItem): boolean => {
  const type = item.content_type || "";
  return ALLOWED_ATTACHMENT_TYPES.images.includes(type);
};

export const isPdf = (item: AttachmentPreviewItem): boolean => {
  const type = item.content_type || "";
  return ALLOWED_ATTACHMENT_TYPES.pdfs.includes(type);
};

export const isText = (item: AttachmentPreviewItem): boolean => {
  const type = item.content_type || "";
  return ALLOWED_ATTACHMENT_TYPES.text.includes(type);
};

// Excel spreadsheets (.xlsx / .xls) — matched by MIME type, with a filename
// fallback for cases where the content_type is missing or generic.
export const isSpreadsheet = (item: AttachmentPreviewItem): boolean => {
  const type = item.content_type || "";
  if (ALLOWED_ATTACHMENT_TYPES.sheets.includes(type)) return true;
  const name = (item.file_name || "").toLowerCase();
  return name.endsWith(".xlsx") || name.endsWith(".xls");
};

export const buildGoogleViewerUrl = (url: string): string => {
  const decoded = decodeURIComponent(url);
  return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(
    decoded
  )}`;
};

export const getTagBgColor = (colour: string) => {
  if (!colour) return "";

  return colour === "#E9FAEF"
    ? `bg-tag-green-bg dark:bg-tag-green-bg-dk border border-tag-green-stroke dark:border-tag-green-stroke-dk`
    : colour === "#FFF6E6"
      ? `bg-tag-yellow-bg dark:bg-tag-yellow-bg-dk border border-tag-yellow-stroke dark:border-tag-yellow-stroke-dk`
      : colour === "#FFDEDE"
        ? `bg-tag-red-bg dark:bg-tag-red-bg-dk border border-tag-red-stroke dark:border-tag-red-stroke-dk`
        : `bg-transparent dark:bg-transparent border border-border-gray dark:border-border-gray-dk`;
};

export const mapChunksToSources = (
  data: Array<{
    document_name: string;
    document_website: string;
    document_id: string;
    document_status: string;
    chunks: Array<{
      id: string;
      chunk_id: string;
      content: string;
      updated_at: string;
      editing: boolean;
      updated_by: string;
      question: string;
      answer: string;
      status: string;
      namespace: {
        name: string;
        id: string;
      };
    }>;
  }>
): FaqSource[] => {
  const sources: FaqSource[] = [];

  data.forEach((doc) => {
    let sourceCounter = 1;
    doc.chunks.forEach((chunk) => {
      sources.push({
        id: chunk?.id,
        chunk_id: chunk?.chunk_id,
        draft_id: chunk?.id,
        status: chunk?.status,
        name: `Source ${sourceCounter}`,
        content: chunk?.content,
        timestamp: new Date(chunk?.updated_at)?.toLocaleTimeString(),
        isEditing: chunk?.editing,
        documentName: doc?.document_name,
        websiteName: doc?.document_website,
        documentId: doc?.document_id,
        documentStatus: doc?.document_status,
        lastEditedDate: `${new Date(chunk?.updated_at).toLocaleDateString([], {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })}, ${new Date(chunk?.updated_at)
          .toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          .toUpperCase()}`,
        lastEditedTime: new Date(chunk?.updated_at)?.toLocaleTimeString(),
        updatedBy: chunk?.updated_by,
        question: chunk?.question,
        answer: chunk?.answer,
        property_id: chunk?.namespace?.id,
        property_name: chunk?.namespace?.name,
      });
      sourceCounter++;
    });
  });

  return sources;
};

export const transformLeadList = (
  lead: ReduxLead,
  communicationMode: { value: string }
) => {
  return {
    ...lead,
    leadId: lead.lead_id,
    leadName: lead?.name || "Anonymous",
    isOnline:
      communicationMode.value === "email"
        ? false
        : lead.is_online_on_whatsapp ||
          lead.is_online_on_widget ||
          lead.is_online_on_fb_messenger ||
          lead.is_online_on_instagram,
    outboundModalities: lead.outbound_channel_tracking || {},
    inboundModalities: lead.channel_tracking || {},
    isHumanAssisted: lead.agent_override,
    sourceCountry: lead.source_country_alpha2,
    latestMessage: lead.latest_chat,
    isSecondaryLead: lead.original_lead_id ? true : false,
    sessionCount: lead?.session_count || 0,
    messageCount: lead?.message_body_count || 0,
    hasFeedback: lead?.feedback?.average_score || 0,
    timestamp: lead?.latest_chat_timestamp,
  };
};

/**
 * `/users/v2` returns each user nested under `user_list[].user`; the mention picker wants a flat
 * row. A row with no id can't be tagged, so it's dropped rather than rendered as a dead entry.
 * The endpoint is already asked for active users only, so status isn't re-checked here.
 */
export const transformMentionableUsers = (
  userList?: V2UserListEntry[],
): MentionableUser[] =>
  (userList ?? [])
    .filter((entry) => Boolean(entry?.user?.id))
    .map((entry) => ({
      _id: entry?.user?.id as string,
      name: entry?.user?.name ?? "",
      email: entry?.user?.email ?? "",
      profile_picture: entry?.user?.profile_picture ?? null,
    }));

export const transformSearchedLead = (lead: ReduxLead) => {
  return {
    ...lead,
    leadId: lead.lead_id,
    leadName: lead?.name || "Anonymous",
    isOnline:
      lead.is_online_on_whatsapp ||
      lead.is_online_on_widget ||
      lead.is_online_on_fb_messenger ||
      lead.is_online_on_instagram,
    outboundModalities: lead.outbound_channel_tracking || {},
    inboundModalities: lead.channel_tracking || {},
    isHumanAssisted: lead.agent_override,
    sourceCountry: lead.source_country_alpha2,
    latestMessage: lead.latest_chat,
    isSecondaryLead: lead.original_lead_id ? true : false,
    sessionCount: lead.session_count,
    messageCount: lead?.message_body_count || 0,
    hasFeedback: lead?.feedback?.average_score || 0,
    timestamp: lead?.latest_chat_timestamp,
  };
};

export const convertHtmlToPlainText = (htmlText: string): string => {
  return htmlText
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div)>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
};

// export const resetModalityAfterMention = ({
//   selectedCommunicationInboxModality,
//   getAvailableInboxModalities,
//   setSelectedCommunicationInboxModality,
// }: ResetModalityAfterMentionParams): void => {
//   if (selectedCommunicationInboxModality === "mentions") {
//     const availableModalities = getAvailableInboxModalities();
//     const defaultModality = availableModalities?.filter(
//       (m) => m?.value !== "mentions"
//     )[0];
//     console.log("defaultModality", defaultModality);
//     if (defaultModality) {
//       setSelectedCommunicationInboxModality(defaultModality?.value);
//     } else {
//       setSelectedCommunicationInboxModality("mentions");
//     }
//   }
// };

export const getPauseAiModalState = (
  modality: string,
  leadDetails: LeadDetails | null
) => {
  if (modality === "widget" && leadDetails?.enabled_channels?.widget) {
    return true;
  } else if (
    modality === "whatsapp" &&
    leadDetails?.enabled_channels?.whatsapp
  ) {
    return true;
  } else if (modality === "email" && leadDetails?.enabled_channels?.email) {
    return true;
  } else if (modality === "call" && leadDetails?.enabled_channels?.call) {
    return true;
  } else if (modality === "sms" && leadDetails?.enabled_channels?.sms) {
    return true;
  } else if (
    modality === "fb_messenger" &&
    leadDetails?.enabled_channels?.fb_messenger
  ) {
    return true;
  } else if (
    modality === "instagram" &&
    leadDetails?.enabled_channels?.instagram
  ) {
    return true;
  }
  return false;
};

export const getModalityName = (modality: string) => {
  if (modality === "widget") {
    return "VerbaChat";
  } else if (modality === "whatsapp") {
    return "VerbaMessenger";
  } else if (modality === "email") {
    return "VerbaMail";
  } else if (modality === "call") {
    return "VerbaCall";
  } else if (modality === "sms") {
    return "VerbaSMS";
  } else if (modality === "fb_messenger") {
    return "Facebook Messenger";
  } else if (modality === "instagram") {
    return "Instagram DMs";
  }
  return modality;
};

export type GroupedMessage =
  | Message
  | CallMessage
  | FeedbackMessage
  | EmailThread;

export const groupMessagesWithEmailThreads = (
  messages: (Message | CallMessage | FeedbackMessage)[]
): GroupedMessage[] => {
  if (!messages || messages.length === 0) return [];

  const threadMap = new Map<string, Message[]>();
  const result: GroupedMessage[] = [];
  const processedThreadIds = new Set<string>();

  const seenMessageIds = new Set<string>();

  messages.forEach((message) => {
    const isEmailMessage =
      "channel" in message &&
      message.channel === "email" &&
      "nylas_thread_id" in message &&
      (message as Message).nylas_thread_id;

    if (isEmailMessage) {
      const emailMsg = message as Message;
      const threadId = emailMsg.nylas_thread_id!;
      const msgId = emailMsg.message_id;

      if (seenMessageIds.has(msgId)) return;
      seenMessageIds.add(msgId);

      if (!threadMap.has(threadId)) {
        threadMap.set(threadId, []);
      }
      threadMap.get(threadId)!.push(emailMsg);
    }
  });

  const processedMessageIds = new Set<string>();

  messages.forEach((message) => {
    const isEmailMessage =
      "channel" in message &&
      message.channel === "email" &&
      "nylas_thread_id" in message &&
      (message as Message).nylas_thread_id;

    if (isEmailMessage) {
      const emailMsg = message as Message;
      const threadId = emailMsg.nylas_thread_id!;

      if (!processedThreadIds.has(threadId)) {
        processedThreadIds.add(threadId);
        const threadMessages = threadMap.get(threadId)!;

        threadMessages.sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        if (threadMessages.length > 1) {
          const emailThread: EmailThread = {
            type: "email_thread",
            nylas_thread_id: threadId,
            subject: threadMessages[0].subject || null,
            messages: threadMessages,
            message_id: threadMessages[0].message_id,
            timestamp: threadMessages[0].timestamp,
            channel: "email",
          };
          result.push(emailThread);
        } else {
          result.push(threadMessages[0]);
        }
      }
    } else {
      const msgId =
        "message_id" in message
          ? message.message_id
          : "id" in message
            ? (message as CallMessage).id
            : "";

      if (msgId && processedMessageIds.has(msgId)) return;
      if (msgId) processedMessageIds.add(msgId);

      result.push(message);
    }
  });

  result.sort((a, b) => {
    const getFirstTimestamp = (msg: GroupedMessage): string => {
      if ("type" in msg && msg.type === "email_thread") {
        return msg.timestamp;
      }
      if ("messages" in msg && msg.channel === "call") {
        return msg.messages?.[0]?.timestamp || "";
      }
      return (msg as Message | FeedbackMessage).timestamp || "";
    };

    const timeA = new Date(getFirstTimestamp(a)).getTime();
    const timeB = new Date(getFirstTimestamp(b)).getTime();
    return timeA - timeB;
  });

  return result;
};
